import { BiddingConfiguration, OpenShiftPolicyEnum } from 'types/graphql';
import { UseFormBehaviorsEventType } from 'hooks/useFormBehaviors/useFormBehaviors.types';

export const getIsBiddingPosition = (
  openShiftPolicy: OpenShiftPolicyEnum | null | undefined,
  isShiftBiddingFeatureEnabled: boolean,
): boolean => {
  return openShiftPolicy === OpenShiftPolicyEnum.bidding_required && isShiftBiddingFeatureEnabled;
};

export const getBiddingOvertimeDefault = (
  biddingConfiguration?: Partial<Pick<BiddingConfiguration, 'biddingOvertimeDefault'>> | null,
): boolean => biddingConfiguration?.biddingOvertimeDefault ?? true;

export const getDefaultAllowOvertime = ({
  isBidding,
  biddingOvertimeDefault,
  fallback = false,
}: {
  isBidding: boolean;
  biddingOvertimeDefault: boolean;
  fallback?: boolean;
}): boolean => (isBidding ? biddingOvertimeDefault : fallback);

export const buildPositionChangeEvents = ({
  e,
  openShiftPolicy,
  isShiftBiddingFeatureEnabled,
  allowOvertimeFormFieldName,
  autoBidFlagFormFieldName,
  biddingOvertimeDefault,
  resets = [],
}: {
  e: UseFormBehaviorsEventType;
  openShiftPolicy: OpenShiftPolicyEnum | null | undefined;
  isShiftBiddingFeatureEnabled: boolean;
  allowOvertimeFormFieldName: string;
  autoBidFlagFormFieldName: string;
  biddingOvertimeDefault: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  resets?: { name: string; value?: any }[]; // fields to reset when position changes
}) => {
  const events = [e];
  const isBidding = getIsBiddingPosition(openShiftPolicy, isShiftBiddingFeatureEnabled);

  events.push({
    target: {
      name: allowOvertimeFormFieldName,
      value: getDefaultAllowOvertime({ isBidding, biddingOvertimeDefault }),
    },
  });
  events.push({ target: { name: autoBidFlagFormFieldName, value: isBidding } });

  resets.forEach(({ name, value }) => {
    events.push({ target: { name, value: value ?? '' } });
  });

  return events;
};

export const buildTeamMemberChangeEvents = ({
  e,
  openShiftPolicy,
  isShiftBiddingFeatureEnabled,
  allowOvertimeFormFieldName,
  autoBidFlagFormFieldName,
  initialAllowOvertime,
  biddingOvertimeDefault,
  isPositionChanged = false,
  isTeamMemberChanged = false,
  preserveAllowOvertimeOnAssign = false,
}: {
  e: UseFormBehaviorsEventType;
  openShiftPolicy: OpenShiftPolicyEnum | null | undefined;
  isShiftBiddingFeatureEnabled: boolean;
  allowOvertimeFormFieldName: string;
  autoBidFlagFormFieldName: string;
  initialAllowOvertime?: boolean;
  biddingOvertimeDefault: boolean;
  isPositionChanged?: boolean;
  isTeamMemberChanged?: boolean;
  preserveAllowOvertimeOnAssign?: boolean;
}) => {
  const events = [e];
  const hasTeamMemberAssigned = !!e?.target?.value;
  const isBidding = getIsBiddingPosition(openShiftPolicy, isShiftBiddingFeatureEnabled);

  if (hasTeamMemberAssigned) {
    if (!preserveAllowOvertimeOnAssign) {
      events.push({ target: { name: allowOvertimeFormFieldName, value: false } });
    }
    events.push({ target: { name: autoBidFlagFormFieldName, value: false } });

    events.push({ target: { name: 'byMultipleFields', value: e.target.value } });
  } else {
    // Team member cleared - back to open shift state
    events.push({ target: { name: 'byMultipleFields', value: '' } });

    if (isBidding) {
      events.push({ target: { name: allowOvertimeFormFieldName, value: biddingOvertimeDefault } });
      events.push({ target: { name: autoBidFlagFormFieldName, value: true } });
    } else if (!isPositionChanged && !isTeamMemberChanged && initialAllowOvertime !== undefined) {
      // Non-bidding position: restore initial value only if nothing has changed
      events.push({ target: { name: allowOvertimeFormFieldName, value: initialAllowOvertime } });
    }
  }

  return events;
};
